import { useEffect, useState } from "react";
import { api, type AppConfig, type ReferralView } from "../api";
import { Overlay, Skeleton } from "../ui";

interface Props {
  config: AppConfig | null;
  onClose: () => void;
  showToast: (t: string) => void;
  onShop: () => void;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Реферальная программа: код, ссылка, награды по тарифам и история начислений. */
export function Referral({ config, onClose, showToast, onShop }: Props) {
  const [data, setData] = useState<ReferralView | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    api.referral().then(
      (r) => alive && setData(r),
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
  }, []);

  const shareLink = config?.botUsername && data ? `https://t.me/${config.botUsername}?start=ref_${data.code}` : null;

  const copy = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      showToast("Ссылка скопирована");
    } catch {
      showToast(shareLink);
    }
  };

  return (
    <Overlay title="Реферальная программа" onClose={onClose} tone="light">
      <div className="sheetbody">
        {!data && !failed && <Skeleton rows={4} tall />}
        {failed && <p className="sub">Не удалось загрузить. Попробуйте ещё раз.</p>}
        {data && (
          <>
            <div className="card">
              <h3>Твой код</h3>
              <div className="row" style={{ alignItems: "center", gap: 8 }}>
                <b style={{ fontSize: 24, letterSpacing: 2 }}>{data.code}</b>
              </div>
              {shareLink && (
                <button className="primary" style={{ width: "100%", marginTop: 10 }} onClick={() => void copy()}>
                  Скопировать ссылку
                </button>
              )}
            </div>

            <div className="card">
              <h3>Всего заработано</h3>
              <p className="sub">
                <b style={{ fontSize: 22 }}>{data.totalCoinsEarned}</b> монет за {data.history.length} {plural(data.history.length)}
              </p>
              <button className="primary" style={{ width: "100%", marginTop: 10 }} onClick={onShop}>
                Обменять на подарки
              </button>
            </div>

            <div className="card">
              <h3>Как это работает</h3>
              <p className="sub">
                1. Отправьте свой код или ссылку другу.
                <br />
                2. Друг открывает бота по вашей ссылке и вводит дату рождения.
                <br />
                3. Когда друг впервые оплачивает тариф — вам начисляются монеты.
              </p>
            </div>

            <div className="card">
              <h3>Награда за тариф</h3>
              <div className="row">
                <span>Базовый</span>
                <b>+{data.rewards.basic}</b>
              </div>
              <div className="row">
                <span>Полный</span>
                <b>+{data.rewards.full}</b>
              </div>
            </div>

            <div className="card">
              <h3>История приглашений</h3>
              {data.history.length === 0 ? (
                <p className="sub">Пока никто не оплатил тариф по вашей ссылке.</p>
              ) : (
                data.history.map((h, i) => (
                  <div className="row" key={i}>
                    <span>{h.name ?? "Без имени"}</span>
                    <span>
                      <b>+{h.amount}</b> <span className="sub">· {formatDate(h.createdAt)}</span>
                    </span>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </Overlay>
  );
}

function plural(n: number): string {
  const a = n % 100;
  const b = n % 10;
  if (a >= 11 && a <= 14) return "раз";
  if (b === 1) return "раз";
  if (b >= 2 && b <= 4) return "раза";
  return "раз";
}
